import { useCallback } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";

import { useAuth } from "../../context/AuthContext";
import routeNames from "../../constants/route-names";
import { useGetUserQuery } from "../../graphql/queries/getUser.generated";

interface PrivateRouteProps {
  children: any;
  requireHighnoteAdmin?: boolean;
  requireWorkspaceAdmin?: boolean;
  redirectPath?: string;
}

const PrivateRoute = (props: PrivateRouteProps) => {
  const {
    children,
    requireHighnoteAdmin,
    requireWorkspaceAdmin,
    redirectPath,
  } = props;
  const location = useLocation();
  const { loggedUser, refreshToken, setLoggedUserData } = useAuth();

  const noPermissionsRequired =
    requireHighnoteAdmin === undefined && requireWorkspaceAdmin === undefined;

  const handleOnErrorsGetUsers = useCallback(
    (error: ApolloError) => {
      console.log("Error Get Users: ", error);

      // Silently attempt to refresh token on auth errors without changing UI flow
      if (
        error.message.includes("JWT") ||
        error.message.includes("token") ||
        error.message.includes("authentication") ||
        error.message.includes("authorization")
      ) {
        if (refreshToken) {
          refreshToken().catch((err) => {
            console.error("Token refresh failed silently:", err);
          });
        }
      }
    },
    [refreshToken]
  );

  const { data, loading } = useGetUserQuery({
    skip: !loggedUser?.uid,
    variables: {
      id: loggedUser?.uid,
    },
    errorPolicy: "all",
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    onError: handleOnErrorsGetUsers,
    onCompleted: (data) => {
      if (setLoggedUserData) {
        setLoggedUserData(data?.users_by_pk);
      }
    },
  });

  // First check if user is authenticated with Firebase
  if (!loggedUser) {
    // Save the attempted URL for redirecting after login
    return (
      <Navigate to={routeNames.LOGIN} state={{ from: location }} replace />
    );
  }

  // If no permissions are required we only check basic authentication, allow access
  if (noPermissionsRequired) {
    return <>{children}</>;
  } else {
    // Show loading state while fetching user data
    if (loading) {
      return (
        <Box
          sx={{
            height: "80%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    if (data?.users_by_pk) {
      const { isHighnoteAdmin, isWorkspaceAdmin } = data?.users_by_pk || {};
      // Determine redirect path based on user admin status
      const finalRedirectPath = isHighnoteAdmin 
        ? routeNames.AI_CHATS 
        : routeNames.AI_CHATS_NONADMIN

      // Highnote Admin is the super user with all the permissions
      if (isHighnoteAdmin) {
        return children;
      } else if (requireHighnoteAdmin) {
        return <Navigate to={redirectPath || finalRedirectPath} />;
      }

      if (requireWorkspaceAdmin) {
        if (isWorkspaceAdmin) {
          return children;
        } else {
          return <Navigate to={redirectPath || finalRedirectPath} />;
        }
      }
    } else {
      const finalRedirectPath = routeNames.AI_CHATS_NONADMIN;
      return <Navigate to={redirectPath || finalRedirectPath} />;
    }
  }
};

export default PrivateRoute;